import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import SEO from '../components/SEO';

interface RenewInfo {
  businessName: string;
  plan: string;
  billingCycle: string;
  amount: number;
  phone: string;
  needsPlan?: boolean;
}

// Public, no-login renewal page — the link WhatsApp/email reminders send
// (see modules/payments.js's renewalLink) since Paynow/EcoCash payments
// don't auto-renew and asking someone to log in, find Billing, and re-pick
// their plan from a WhatsApp message is exactly the friction that lets a
// renewal slip past the overdue window.
export default function Renew() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';

  const [info, setInfo] = useState<RenewInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState<'web' | 'ecocash' | 'innbucks'>('web');
  const [phone, setPhone] = useState('');
  const [instructions, setInstructions] = useState('');

  useEffect(() => {
    if (!uid || !token) { setError('This link is invalid.'); setLoading(false); return; }
    apiFetch(`/api/renew/info?uid=${encodeURIComponent(uid)}&token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) { setError(d.error || 'This link is invalid or has expired.'); }
        else if (d.needsPlan) { setError("This account doesn't have an active paid plan to renew — please log in and choose a plan instead."); }
        else { setInfo(d); setPhone(d.phone || ''); }
        setLoading(false);
      })
      .catch(() => { setError('Could not load renewal details. Please try again.'); setLoading(false); });
  }, [uid, token]);

  async function handlePay() {
    if (!info) return;
    if (method !== 'web') {
      const phoneRegex = method === 'ecocash' ? /^(07|2637)[0-9]{8}$/ : /^(078|26378)[0-9]{8}$/;
      if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
        setError(`Please enter a valid ${method === 'ecocash' ? 'EcoCash' : 'OneMoney'} number`);
        return;
      }
    }
    setError('');
    setPaying(true);
    try {
      const res = await apiFetch('/api/renew/pay', {
        method: 'POST',
        body: JSON.stringify({ uid, token, method, phone: method !== 'web' ? phone.replace(/\s/g, '') : undefined }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Payment could not be started.'); setPaying(false); return; }
      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else if (data.instructions) {
        setInstructions(data.instructions);
        setPaying(false);
      }
    } catch {
      setError('Could not reach the server. Please try again.');
      setPaying(false);
    }
  }

  return (
    <>
      <SEO title="Renew your plan | Autopilot" description="Renew your Autopilot subscription." noindex />
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1E293B 0%, #1E293B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'white', borderRadius: '24px', padding: '48px', maxWidth: '440px', width: '100%', boxShadow: '0 20px 35px -10px rgba(0,0,0,0.2)' }}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>🔄</div>
            <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1f2937' }}>Renew your plan</h1>
          </div>

          {loading && <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading…</p>}

          {!loading && error && !info && (
            <p style={{ textAlign: 'center', color: '#dc2626', fontSize: '14px' }}>{error}</p>
          )}

          {!loading && info && (
            <>
              <div style={{ background: '#f3f4f6', borderRadius: '14px', padding: '18px', marginBottom: '22px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                  {info.businessName ? `${info.businessName} — ` : ''}{info.plan} plan
                </div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>${info.amount}</div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>/{info.billingCycle === 'yearly' ? 'year' : 'month'}</div>
              </div>

              {instructions ? (
                <p style={{ textAlign: 'center', color: '#059669', fontSize: '14px', fontWeight: 500 }}>{instructions}</p>
              ) : (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Pay with</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {(['web', 'ecocash', 'innbucks'] as const).map(m => (
                        <button
                          key={m}
                          onClick={() => setMethod(m)}
                          style={{
                            flex: 1, padding: '10px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            border: method === m ? '2px solid #5B4FE8' : '1px solid #e5e7eb',
                            background: method === m ? '#eef2ff' : 'white', color: method === m ? '#5B4FE8' : '#6b7280',
                          }}
                        >
                          {m === 'web' ? 'Card / Paynow' : m === 'ecocash' ? 'EcoCash' : 'OneMoney'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {method !== 'web' && (
                    <div style={{ marginBottom: '16px' }}>
                      <input
                        type="tel"
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        placeholder={method === 'ecocash' ? '077xxxxxxx' : '078xxxxxxx'}
                        style={{ width: '100%', padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  )}

                  {error && <p style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}

                  <button
                    onClick={handlePay}
                    disabled={paying}
                    style={{
                      width: '100%', padding: '14px', background: paying ? '#374151' : '#1E293B', color: 'white',
                      border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600',
                      cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.7 : 1,
                    }}
                  >
                    {paying ? 'Processing…' : `Pay $${info.amount} now`}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
